import React, { useState } from "react";
import { toast } from "sonner";
import {
  ExternalLink,
  Music2,
  FileText,
  Music,
  Download,
  Sparkles,
  Scissors,
  Palette,
  Copy,
  Check,
} from "lucide-react";

/**
 * AIResourcesSection
 *
 * Sezione mostrata nella dashboard cliente (Free + Pro) con risorse di
 * intelligenza artificiale esterne consigliate dall'agenzia.
 *
 * Composta da 2 sotto-blocchi:
 *
 *   ─── Genera musica ────────────────────────────────────────────────
 *   1) CTA Suno → genera brano musicale royalty-free
 *   2) Tutorial "Come usare Suno in 5 step"
 *
 *   ─── Prompt pronti per ChatGPT / Gemini ──────────────────────────
 *   3) Anteprima tagli capelli (8 varianti su una foto)
 *   4) Anteprima colori capelli (11 varianti su una foto)
 *
 * I prompt sono in inglese (più affidabili sui modelli) e copy-paste.
 * Nessun backend: pura "risorsa educativa" che apre tool esterni.
 */

const SUNO_URL = "https://suno.com/";
const GEMINI_URL = "https://gemini.google.com/";
const CHATGPT_URL = "https://chat.openai.com/";

// === PROMPTS ================================================================
const PROMPT_CUTS = `Using this portrait, create a diagram-first hairstyle analysis. Generate multiple female haircut variations applied to the same face, including: pixie cut, bob, lob (long bob), shoulder-length layers, long straight, long wavy, curtain bangs, and updo. Arrange them in a clean comparison layout (grid or radial). Highlight the most flattering hairstyles subtly (e.g. with a soft glow or outline). Keep text minimal — only short labels per style. No paragraphs. Aspect ratio 3:4.`;

const PROMPT_COLORS = `Using this portrait, create a diagram-first hair color analysis. Keep the exact same haircut and face unchanged. Generate multiple realistic hair color variations applied to the same person, including: natural black, dark brown, chestnut brown, warm auburn, honey blonde, ash blonde, platinum blonde, strawberry blonde, copper red, dark burgundy, and balayage (dark roots to caramel ends). Arrange them in a clean grid or radial comparison layout. Highlight the 2–3 most flattering colors for this skin tone subtly (e.g. soft glow or golden outline). Keep text minimal — only the color name as a short label per swatch. No paragraphs. Aspect ratio 3:4.`;

// === LOGO SVGs (inline per evitare richieste esterne) =======================
function SunoMark({ className = "h-8 w-8" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Suno" role="img">
      <rect width="64" height="64" rx="14" fill="currentColor" />
      <g fill="#fff">
        <rect x="14" y="26" width="6" height="12" rx="3" />
        <rect x="24" y="18" width="6" height="28" rx="3" />
        <rect x="34" y="22" width="6" height="20" rx="3" />
        <rect x="44" y="28" width="6" height="8" rx="3" />
      </g>
    </svg>
  );
}

const TUTORIAL_STEPS = [
  { n: 1, icon: ExternalLink, title: "Crea l'account gratuito", body: "Vai su suno.com e iscriviti con email o Google. Bastano 30 secondi." },
  { n: 2, icon: Sparkles, title: "Ricevi crediti gratis ogni giorno", body: "Suno ti regala 50 crediti al giorno (≈ 10 canzoni gratuite/giorno). Si rinnovano automaticamente." },
  { n: 3, icon: FileText, title: "Scrivi il testo del brano", body: "Inserisci le parole della canzone. Se non sai cosa scrivere, chiedi a ChatGPT (es. «scrivimi un testo da 30 secondi su un salone di parrucchieri a Milano, mood energico»)." },
  { n: 4, icon: Music, title: "Scegli il genere musicale", body: "Pop, lo-fi, rock, ambient, hip-hop, elettronica… o descrivi il mood (es. «chill, estate, sole»)." },
  { n: 5, icon: Download, title: "Scarica la canzone", body: "Suno genera in 30-60 secondi. Scarichi l'MP3, lo carichi su Reels / TikTok / Shorts e non ti silenziano mai più il video." },
];

// === Bottoncini esterni Gemini / ChatGPT ====================================
function AIButton({ href, label, primary, testId }) {
  const base =
    "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium transition-colors no-underline";
  const cls = primary
    ? `${base} bg-[var(--mp-ink)] text-white hover:bg-black`
    : `${base} border border-[var(--mp-ink)] bg-white text-[var(--mp-ink)] hover:bg-[var(--mp-dash-bg)]`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls}>
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

// === Box "Prompt ChatGPT/Gemini" =============================================
function PromptCard({
  testId,
  icon: Icon,
  label,
  title,
  description,
  thumbBase,
  prompt,
  promptLabel,
  steps,
}) {
  const [copied, setCopied] = useState(false);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      toast.success("Prompt copiato ✓ — ora incollalo su Gemini o ChatGPT");
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      toast.error("Impossibile copiare automaticamente. Selezionalo e copialo a mano.");
    }
  };

  return (
    <div
      data-testid={testId}
      className="rounded-3xl border border-[var(--mp-ink)] bg-white p-5 sm:p-6 shadow-brutal-sm flex flex-col gap-4"
    >
      {/* Header con thumbnail */}
      <div className="flex gap-4 items-start">
        <picture className="shrink-0">
          <source
            type="image/webp"
            srcSet={`/img/${thumbBase}-480.webp 480w, /img/${thumbBase}-800.webp 800w, /img/${thumbBase}-1200.webp 1200w`}
            sizes="(max-width: 1023px) 35vw, 180px"
          />
          <source
            type="image/jpeg"
            srcSet={`/img/${thumbBase}-480.jpg 480w, /img/${thumbBase}-800.jpg 800w, /img/${thumbBase}-1200.jpg 1200w`}
            sizes="(max-width: 1023px) 35vw, 180px"
          />
          <img
            src={`/img/${thumbBase}-480.jpg`}
            alt={`Esempio: ${title}`}
            width="480"
            height={thumbBase === "cuts" ? 640 : 599}
            loading="lazy"
            decoding="async"
            className="w-[140px] sm:w-[180px] h-auto rounded-xl border border-[var(--mp-border)] object-cover"
            data-testid={`${testId}-thumb`}
          />
        </picture>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            <Icon className="h-3 w-3" />
            {label}
          </div>
          <div className="font-display text-xl tracking-tight mt-1">{title}</div>
          <p className="mt-2 text-[12.5px] text-[var(--mp-muted)] leading-snug">
            {description}
          </p>
        </div>
      </div>

      {/* 4 step rapidi */}
      <ol className="space-y-1.5 text-[12.5px] text-[var(--mp-ink)]">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="font-bold text-[var(--mp-muted)] shrink-0">{i + 1}.</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>

      {/* Prompt copy box */}
      <div className="rounded-xl bg-[var(--mp-dash-bg)] border border-[var(--mp-border)] p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            {promptLabel}
          </div>
          <button
            type="button"
            onClick={copyPrompt}
            data-testid={`${testId}-copy-btn`}
            className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[var(--mp-ink)] text-white text-[11px] font-medium hover:bg-black transition-colors"
            aria-label="Copia prompt"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copiato" : "Copia prompt"}
          </button>
        </div>
        <pre
          data-testid={`${testId}-prompt-text`}
          className="text-[11.5px] leading-relaxed text-[var(--mp-ink)] whitespace-pre-wrap font-mono max-h-32 overflow-y-auto"
        >
          {prompt}
        </pre>
      </div>

      {/* CTAs Gemini (primary) + ChatGPT (secondary) */}
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[var(--mp-border)]">
        <AIButton
          href={GEMINI_URL}
          label="Apri Gemini (consigliato)"
          primary
          testId={`${testId}-gemini-btn`}
        />
        <AIButton
          href={CHATGPT_URL}
          label="Apri ChatGPT"
          primary={false}
          testId={`${testId}-chatgpt-btn`}
        />
      </div>
    </div>
  );
}

export default function AIResourcesSection() {
  return (
    <section data-testid="ai-resources-section" className="space-y-6">
      {/* Header sezione */}
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-[var(--mp-yellow)] grid place-items-center">
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
            Risorse utili
          </div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight">
            Strumenti di intelligenza artificiale
          </h2>
          <p className="mt-1 text-sm text-[var(--mp-muted)] max-w-2xl">
            Tool esterni che ti consigliamo per arricchire i tuoi contenuti
            social e per &quot;wow-effect&quot; con le tue clienti in salone. Niente
            costi nascosti — sono gratuiti o freemium.
          </p>
        </div>
      </div>

      {/* ─── SOTTO-BLOCCO 1: GENERA MUSICA ─────────────────────────── */}
      <div className="space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)] pl-1">
          ♪ Genera musica royalty-free
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Box 1 — CTA Suno */}
          <a
            href={SUNO_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ai-resource-suno-cta"
            className="group rounded-3xl border border-[var(--mp-ink)] bg-white p-6 sm:p-7 shadow-brutal-sm hover:shadow-brutal transition-shadow flex flex-col gap-4 no-underline"
          >
            <div className="flex items-start justify-between gap-3">
              <SunoMark className="h-12 w-12 text-[var(--mp-ink)] shrink-0" />
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                <Music2 className="h-3 w-3" />
                Musica AI
              </span>
            </div>
            <div className="flex-1">
              <div className="font-display text-2xl tracking-tight text-[var(--mp-ink)]">
                Genera il tuo brano musicale
              </div>
              <p className="mt-2 text-sm text-[var(--mp-muted)]">
                Crea la colonna sonora dei tuoi Reel <strong>senza problemi di copyright</strong>{" "}
                e senza più rischio di video silenziati dai social. Un brano interamente tuo,
                in 1 minuto.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-[var(--mp-border)]">
              <div className="text-[11px] text-[var(--mp-muted)]">
                <strong className="text-[var(--mp-ink)]">10 canzoni gratis</strong> al giorno
                · Iscrizione in 30 sec
              </div>
              <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--mp-ink)] text-white text-sm font-medium group-hover:bg-black transition-colors">
                Apri Suno
                <ExternalLink className="h-3.5 w-3.5" />
              </span>
            </div>
          </a>

          {/* Box 2 — Tutorial Suno */}
          <div
            data-testid="ai-resource-suno-tutorial"
            className="rounded-3xl border border-[var(--mp-ink)] bg-[var(--mp-dash-bg)] p-6 sm:p-7 shadow-brutal-sm flex flex-col gap-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                  Tutorial · 5 passi
                </div>
                <div className="font-display text-2xl tracking-tight mt-1">Come usare Suno</div>
              </div>
              <SunoMark className="h-10 w-10 text-[var(--mp-ink)] opacity-50 shrink-0" />
            </div>
            <ol className="space-y-3 flex-1">
              {TUTORIAL_STEPS.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.n}
                    data-testid={`ai-resource-suno-step-${s.n}`}
                    className="flex items-start gap-3"
                  >
                    <div className="h-7 w-7 rounded-full bg-[var(--mp-ink)] text-white grid place-items-center text-[12px] font-bold shrink-0 mt-0.5">
                      {s.n}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-[var(--mp-ink)]">
                        <Icon className="h-3.5 w-3.5" />
                        {s.title}
                      </div>
                      <div className="text-[12.5px] text-[var(--mp-muted)] leading-snug mt-0.5">
                        {s.body}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="pt-2 border-t border-[var(--mp-border)]">
              <a
                href={SUNO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ai-resource-suno-tutorial-cta"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--mp-ink)] text-white text-sm font-medium hover:bg-black transition-colors no-underline"
              >
                Apri Suno e prova subito
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SOTTO-BLOCCO 2: PROMPT CHATGPT / GEMINI ─────────────── */}
      <div className="space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)] pl-1">
          ✨ Prompt pronti per Gemini / ChatGPT — Mostra alla cliente prima del taglio
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <PromptCard
            testId="ai-resource-cuts"
            icon={Scissors}
            label="Anteprima tagli"
            title="8 tagli diversi sulla stessa cliente"
            description="Carica una foto frontale del viso e ottieni una griglia con 8 tagli applicati al suo volto: pixie, bob, lob, layers, lunghi lisci, lunghi mossi, frangia a tendina, raccolto. I più adatti vengono evidenziati."
            thumbBase="cuts"
            promptLabel="Prompt (in inglese, copia e incolla)"
            prompt={PROMPT_CUTS}
            steps={[
              "Apri Gemini (consigliato) o ChatGPT con il bottone qui sotto",
              "Carica una foto della cliente — frontale, viso ben illuminato",
              "Incolla questo prompt e invia",
              "Aspetta 20-60 secondi e mostra la griglia alla cliente sul tablet",
            ]}
          />

          <PromptCard
            testId="ai-resource-colors"
            icon={Palette}
            label="Anteprima colori"
            title="11 colori diversi sullo stesso viso"
            description="Stesso taglio, 11 sfumature applicate: nero, castani, biondi, ramati, balayage. L'AI evidenzia i 2-3 colori che starebbero meglio con la sua carnagione. Perfetto per chi è indeciso prima della tinta."
            thumbBase="colors"
            promptLabel="Prompt (in inglese, copia e incolla)"
            prompt={PROMPT_COLORS}
            steps={[
              "Apri Gemini (consigliato) o ChatGPT con il bottone qui sotto",
              "Carica una foto frontale della cliente, possibilmente con i capelli visibili",
              "Incolla questo prompt e invia",
              "Mostra le 11 varianti alla cliente: spesso aiuta a chiudere la vendita della tinta",
            ]}
          />
        </div>
        <div className="text-[11px] text-[var(--mp-muted)] pl-1">
          <strong>💡 Consigliamo Gemini</strong> per questi prompt: è gratuito (no abbonamento) e
          mantiene meglio il volto della cliente tra le varianti. Su Gemini usa la modalità{" "}
          <em>&quot;Gemini 3 Pro&quot;</em> o <em>&quot;Genera immagine&quot;</em>. Su ChatGPT serve il piano Plus
          per generare immagini.
        </div>
      </div>
    </section>
  );
}
